import { useEffect, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { isTyping } from '@/lib/hotkeys'
import { GO_PREFIX, GO_TIMEOUT_MS, stepGo, type GoState } from '@/lib/goTo'

/**
 * `g` then a letter, for the eleven places the sidebar goes.
 *
 * All the decisions live in `stepGo`, which is pure and tested; this holds the
 * prefix, listens, and navigates. The only thing it adds is the hint below.
 *
 * `useRef` rather than `useState` for the prefix, deliberately. The chord is
 * read and written inside one event handler and must be current on the very
 * next keystroke — state batched into the next render would let a fast typist
 * outrun it, which is exactly the person using a two-key shortcut.
 */
export function GoToShortcut() {
  const navigate = useNavigate()
  const chord = useRef<GoState>(null)
  const [armed, setArmed] = useState(false)

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      // Modifiers belong to the browser, and a field belongs to whoever is
      // typing in it — the same two rules every other shortcut here follows.
      if (event.altKey || event.ctrlKey || event.metaKey) return
      if (event.isComposing) return
      if (isTyping(event.target)) {
        chord.current = null
        setArmed(false)
        return
      }

      const step = stepGo(chord.current, event.key, Date.now())
      chord.current = step.state
      setArmed(step.state !== null)

      // Only swallow the key when it is part of the chord. Anything else has
      // to reach the shortcut that owns it — `c` still captures.
      if (step.state !== null || step.to !== undefined) event.preventDefault()

      if (step.to !== undefined) {
        void navigate({ to: step.to as '/' })
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [navigate])

  /*
   * The prefix has to expire on the clock as well as on the next keystroke.
   * `stepGo` already refuses a stale prefix, so this changes no behaviour —
   * it exists so the hint disappears rather than sitting there implying the
   * app is still waiting.
   */
  useEffect(() => {
    if (!armed) return
    const timer = setTimeout(() => {
      chord.current = null
      setArmed(false)
    }, GO_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [armed])

  if (!armed) return null

  return (
    <div
      // Announced, because someone who cannot see the hint is the person most
      // likely to be navigating by keyboard.
      role="status"
      aria-live="polite"
      className="bg-foreground text-background pointer-events-none fixed bottom-4 left-1/2 z-50 -translate-x-1/2 rounded-md px-3 py-1.5 text-xs shadow-sm"
    >
      <kbd className="font-mono">{GO_PREFIX}</kbd> — now a letter, or Esc
    </div>
  )
}
